import { LocalStorageKey } from "../LocalStorageKey.js";

/**
 * Class to implement the scaling functionality
 * as a singleton, which can be instantiated by
 * any other extension as a dependency
 */
export class Scalings {
    public static readonly default = 100;

    /**
     * The private constructor
     * prevents direct instantiation
     */
    private constructor() {
        // Get the currently applying font-size in order to
        // set it automatically should none be defined
        const _ = this.value;
    }

    /**
     * Method to instantiate the
     * scalings on the current window
     *
     * @returns {Scalings}
     */
    public static use(): Scalings {
        if (!window.scalings) {
            window.scalings = new Scalings();
        }
        return window.scalings;
    }

    /**
     * Method to apply a given font-size
     *
     * @param {number} size
     * @returns {void}
     */
    public set value(size: number) {
        document.documentElement.style.fontSize = `${size}%`;
        localStorage.setItem(LocalStorageKey.Scaling, size.toString());
    }

    /**
     * Method to get the currently configured font-size
     *
     * @returns {number}
     */
    public get value(): number {
        // Try to read the currently applying font-size
        if (/\d+%/.test(document.documentElement.style.fontSize)) {
            const fontSize = ~~(
                document.documentElement.style.fontSize.match(/(\d+)%/)?.[1] ??
                "100"
            );
            this.value = fontSize;
            return fontSize;
        }

        // Try to read the stored font-size as a fallback,
        // otherwise set the default font-size and return it
        const fontSize = localStorage.getItem(LocalStorageKey.Scaling);
        if (fontSize && /\d+/.test(fontSize)) {
            this.value = ~~fontSize;
            return ~~fontSize;
        } else {
            this.value = Scalings.default;
            return Scalings.default;
        }
    }

    /**
     * Method to apply the default scaling
     *
     * @returns {void}
     */
    public initial(): void {
        this.value = Scalings.default;
    }

    /**
     * Method to apply a large scaling
     *
     * @returns {void}
     */
    public large(): void {
        this.value = 125;
    }

    /**
     * Method to apply an extra large scaling
     *
     * @returns {void}
     */
    public extraLarge(): void {
        this.value = 150;
    }
}
